import { FormsOverview } from '@authorizedInstaller/routes/dashboard';
import { CompanyHeader } from '@company/components/display/headers';
import { DashboardCompanyOverview } from '@company/routes';
import { CompaniesList } from '@company/routes/dashboard/CompaniesList';
import { CompanySetup } from '@company/routes/public/Setup/CompanySetup';
import { MobileNotSupported, SplitLayout } from '@components/layout';
import { DashboardLayout } from '@components/layout/dashboard/DashboardLayout';
import { ComponentDetailContextProvider } from '@contentManagement/contexts';
import { ComponentDetail } from '@contentManagement/routes';
import { EventDetail, EventList } from '@event/routes/dashboard';
import { useAuth } from '@hooks/useAuth';
import InboxList from '@inbox/routes/dashboard/InboxList/InboxList';
import { MembersRoutes } from '@members/routes';
import { Forbidden } from '@routes/Forbidden';
import { useGetIsFlagEnabledQuery } from '@services/featureFlag';
import { isMobileOnly } from 'react-device-detect';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { NotFound } from 'src/routes/NotFound';

const DashboardViews = () => {
    const { data: isCompanyEntityEnabled } = useGetIsFlagEnabledQuery('COMPANY_ENTITY');
    const { user } = useAuth();

    return isMobileOnly ? (
        <MobileNotSupported
            title="Dashboard unavailable"
            subtitle="Please visit the dashboard on a larger screen to access it"
        />
    ) : (
        <>
            <Routes>
                <Route
                    path="events"
                    element={
                        <DashboardLayout>
                            <Outlet />
                        </DashboardLayout>
                    }
                >
                    <Route index element={<EventList />} />
                    <Route path=":type/:eventId" element={<EventDetail />} />
                    <Route path="*" element={<NotFound />} />
                </Route>
                <Route
                    path="emails"
                    element={
                        <DashboardLayout>
                            <Outlet />
                        </DashboardLayout>
                    }
                >
                    <Route path="inbox-management" element={<InboxList />} />
                </Route>
                <Route
                    path="authorized-installers"
                    element={
                        <DashboardLayout>
                            <Outlet />
                        </DashboardLayout>
                    }
                >
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<FormsOverview />} />
                </Route>
                {isCompanyEntityEnabled?.is_enabled ? (
                    <Route path="companies" element={<Outlet />}>
                        <Route
                            index
                            element={
                                <DashboardLayout
                                    customHeader={
                                        <div className="bg-white flex items-center px-4 h-[80px]">
                                            <CompanyHeader />
                                        </div>
                                    }
                                >
                                    <DashboardCompanyOverview />
                                </DashboardLayout>
                            }
                        />
                        <Route
                            path="*"
                            element={
                                <DashboardLayout>
                                    <Outlet />
                                </DashboardLayout>
                            }
                        >
                            <Route path="new" element={<CompanySetup />} />
                            <Route path="my-companies" element={<CompaniesList />} />
                            <Route path="*" element={<NotFound />} />
                        </Route>
                    </Route>
                ) : null}
                <Route
                    path="content-management"
                    element={
                        user?.manages_banners ? (
                            <DashboardLayout>
                                <Outlet />
                            </DashboardLayout>
                        ) : (
                            <SplitLayout>
                                <Forbidden />
                            </SplitLayout>
                        )
                    }
                >
                    <Route index element={<Navigate to="overview-banner" replace />} />
                    <Route
                        path="overview-banner"
                        element={
                            <ComponentDetailContextProvider>
                                <ComponentDetail />
                            </ComponentDetailContextProvider>
                        }
                    />
                    <Route path="*" element={<NotFound />} />
                </Route>
                <Route
                    path="members/*"
                    element={
                        <DashboardLayout>
                            <MembersRoutes />
                        </DashboardLayout>
                    }
                />
                <Route
                    path="*"
                    element={
                        <DashboardLayout>
                            <NotFound />
                        </DashboardLayout>
                    }
                />
            </Routes>
        </>
    );
};

export { DashboardViews };
